"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { QuantityField } from "@/components/tools/physics-fields/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  DISTANCE_UNITS,
  type DurationParts,
  lightTime,
  SPEED_UNITS,
  splitDuration,
} from "@/lib/tool-kit/astronomy/distance";
import { SPEED_OF_LIGHT } from "@/lib/tool-kit/physics-fields/constants";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";
import { readQuantity } from "@/lib/tool-kit/physics-fields/input";
import { fromBase } from "@/lib/tool-kit/physics-fields/units";

/** English strings. A translated content file overrides these through `ui`. */
export const LIGHT_YEAR_CONVERTER_STRINGS = {
  example: "Start from an example",
  custom: "Custom distance",
  exampleNames: {
    moon: "Earth to the Moon (mean distance)",
    sun: "Earth to the Sun",
    proxima: "Proxima Centauri, the nearest star",
    galaxy: "Across the Milky Way (about 100,000 light-years)",
  },
  value: "Distance",
  unitFor: "Unit for the distance",
  unitNames: {
    m: "Metres",
    km: "Kilometres",
    mi: "Miles",
    au: "Astronomical units",
    ls: "Light-seconds",
    lmin: "Light-minutes",
    lh: "Light-hours",
    ld: "Light-days",
    ly: "Light-years",
    pc: "Parsecs",
    kpc: "Kiloparsecs",
    mpc: "Megaparsecs",
  },
  sigFigs: "Significant figures",
  sigMessage:
    "one: {count} significant figure|other: {count} significant figures",
  missing: "Enter a distance. Scientific notation such as 9.46e15 works.",
  negative: "A distance cannot be negative.",
  results: "Your distance in other units",
  lightYears: "Light-years",
  parsecs: "Parsecs",
  astronomicalUnits: "Astronomical units",
  kilometres: "Kilometres",
  miles: "Miles",
  lightTime: "Light takes",
  tableHeading: "Every unit",
  unitColumn: "Unit",
  symbolColumn: "Symbol",
  valueColumn: "Value",
  years: "{value} years",
  days: "{value} d",
  hours: "{value} h",
  minutes: "{value} min",
  seconds: "{value} s",
  travelHeading: "How long would it take to travel there?",
  travelFor: "Travelling at",
  speedNames: {
    light: "The speed of light",
    voyager: "Voyager 1 (about 17 km/s)",
    parker: "Parker Solar Probe record (about 192 km/s)",
    jet: "An airliner (900 km/h)",
    walking: "A brisk walk (5 km/h)",
    custom: "Custom speed",
  },
  speed: "Speed",
  speedUnitFor: "Unit for the speed",
  travelTime: "Travel time",
  tooFast: "A speed above the speed of light is not possible.",
  speedMissing: "Enter a speed greater than zero.",
  copy: "Copy result",
  copied: "Copied!",
  summary: "{value} {unit} = {ly} ly = {pc} pc = {au} AU = {km} km.",
  note: "The light-year is the distance light travels in a Julian year (365.25 days), exactly 9,460,730,472,580,800 m. The astronomical unit is exactly 149,597,870,700 m (IAU 2012) and the parsec exactly 648,000/π AU (IAU 2015).",
} as const;

type Strings = typeof LIGHT_YEAR_CONVERTER_STRINGS;
type SigChoice = "4" | "6" | "8" | "10";
const SIG_CHOICES: readonly SigChoice[] = ["4", "6", "8", "10"];

interface Example {
  value: string;
  unit: string;
}

const EXAMPLES: Record<string, Example> = {
  moon: { value: "384400", unit: "km" },
  sun: { value: "1", unit: "au" },
  proxima: { value: "4.2465", unit: "ly" },
  galaxy: { value: "100000", unit: "ly" },
};

interface Speed {
  value: string;
  unit: string;
}

const SPEEDS: Record<string, Speed> = {
  light: { value: "1", unit: "c" },
  voyager: { value: "16.9995", unit: "kms" },
  parker: { value: "192.22", unit: "kms" },
  jet: { value: "900", unit: "kmh" },
  walking: { value: "5", unit: "kmh" },
};

function durationText(
  parts: DurationParts,
  strings: Strings,
  show: (value: number) => string,
  integer: (value: number) => string
): string {
  if (parts.kind === "years") {
    return t(strings.years, { value: show(parts.years) });
  }
  if (parts.kind === "seconds") {
    return t(strings.seconds, { value: show(parts.seconds) });
  }
  const pieces: string[] = [];
  if (parts.days > 0) {
    pieces.push(t(strings.days, { value: integer(parts.days) }));
  }
  if (parts.days > 0 || parts.hours > 0) {
    pieces.push(t(strings.hours, { value: integer(parts.hours) }));
  }
  if (parts.days > 0 || parts.hours > 0 || parts.minutes > 0) {
    pieces.push(t(strings.minutes, { value: integer(parts.minutes) }));
  }
  pieces.push(t(strings.seconds, { value: integer(parts.seconds) }));
  return pieces.join(" ");
}

export default function LightYearConverterWidget() {
  const strings: Strings = useToolUi(LIGHT_YEAR_CONVERTER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [example, setExample] = useState("proxima");
  const [text, setText] = useState("4.2465");
  const [unit, setUnit] = useState("ly");
  const [sigChoice, setSigChoice] = useState<SigChoice>("6");
  const [speedKey, setSpeedKey] = useState("voyager");
  const [speedText, setSpeedText] = useState("16.9995");
  const [speedUnit, setSpeedUnit] = useState("kms");

  const sig = Number(sigChoice);
  const show = (value: number) => formatSig(fmt, value, sig);
  const integer = (value: number) => fmt(value, { maximumFractionDigits: 0 });
  const metres = readQuantity(text, DISTANCE_UNITS, unit);
  const metresPerSecond = readQuantity(speedText, SPEED_UNITS, speedUnit);
  const names = strings.unitNames as Record<string, string>;
  const pickExample = (id: string) => {
    setExample(id);
    const ex = EXAMPLES[id];
    if (ex) {
      setText(ex.value);
      setUnit(ex.unit);
    }
  };
  const pickSpeed = (id: string) => {
    setSpeedKey(id);
    const preset = SPEEDS[id];
    if (preset) {
      setSpeedText(preset.value);
      setSpeedUnit(preset.unit);
    }
  };
  const inUnit = (id: string) =>
    metres === null ? 0 : fromBase(DISTANCE_UNITS, id, metres);
  const symbolOf = (id: string) =>
    DISTANCE_UNITS.find((u) => u.id === id)?.symbol ?? id;
  const travel =
    metres !== null && metresPerSecond !== null && metresPerSecond > 0
      ? metres / metresPerSecond
      : null;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="ly-example"
          label={strings.example}
          onChange={pickExample}
          options={[
            { value: "custom", label: strings.custom },
            ...Object.keys(EXAMPLES).map((id) => ({
              value: id,
              label: (strings.exampleNames as Record<string, string>)[id] ?? id,
            })),
          ]}
          value={example}
        />
        <SelectField
          id="ly-sig"
          label={strings.sigFigs}
          onChange={setSigChoice}
          options={SIG_CHOICES.map((choice) => ({
            value: choice,
            label: tPlural(strings.sigMessage, locale, Number(choice), {
              count: fmt(Number(choice)),
            }).trim(),
          }))}
          value={sigChoice}
        />
        <QuantityField
          id="ly-value"
          label={strings.value}
          onChange={(next) => {
            setExample("custom");
            setText(next);
          }}
          onUnit={setUnit}
          unit={unit}
          unitLabel={strings.unitFor}
          units={DISTANCE_UNITS}
          value={text}
        />
      </div>
      <div aria-live="polite" className="space-y-6">
        {metres === null ? <Notice>{strings.missing}</Notice> : null}
        {metres !== null && metres < 0 ? (
          <ErrorNotice>{strings.negative}</ErrorNotice>
        ) : null}
        {metres !== null && metres >= 0 ? (
          <>
            <section className="space-y-4">
              <h2 className={WIDGET_H2}>{strings.results}</h2>
              <StatGrid>
                <StatCard
                  emphasis
                  label={strings.lightYears}
                  value={show(inUnit("ly"))}
                />
                <StatCard label={strings.parsecs} value={show(inUnit("pc"))} />
                <StatCard
                  label={strings.astronomicalUnits}
                  value={show(inUnit("au"))}
                />
                <StatCard
                  label={strings.kilometres}
                  value={show(inUnit("km"))}
                />
                <StatCard label={strings.miles} value={show(inUnit("mi"))} />
                <StatCard
                  label={strings.lightTime}
                  value={durationText(
                    splitDuration(lightTime(metres)),
                    strings,
                    show,
                    integer
                  )}
                />
              </StatGrid>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy}
                text={t(strings.summary, {
                  value: text.trim(),
                  unit: symbolOf(unit),
                  ly: show(inUnit("ly")),
                  pc: show(inUnit("pc")),
                  au: show(inUnit("au")),
                  km: show(inUnit("km")),
                })}
              />
            </section>
            <section className={`${CARD} space-y-3`}>
              <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
              <div className="overflow-x-auto rounded-lg border border-border bg-card">
                <table className={TABLE}>
                  <thead>
                    <tr>
                      <th className={TH} scope="col">
                        {strings.unitColumn}
                      </th>
                      <th className={TH} scope="col">
                        {strings.symbolColumn}
                      </th>
                      <th className={TH} scope="col">
                        {strings.valueColumn}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {DISTANCE_UNITS.map((u) => (
                      <tr key={u.id}>
                        <td className={TD}>{names[u.id]}</td>
                        <td className={TD}>{u.symbol}</td>
                        <td className={`${TD} font-mono`}>
                          {show(inUnit(u.id))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section className={`${CARD} space-y-4`}>
              <h3 className={WIDGET_H3}>{strings.travelHeading}</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <SelectField
                  id="ly-speed-preset"
                  label={strings.travelFor}
                  onChange={pickSpeed}
                  options={[
                    ...Object.keys(SPEEDS).map((id) => ({
                      value: id,
                      label:
                        (strings.speedNames as Record<string, string>)[id] ??
                        id,
                    })),
                    { value: "custom", label: strings.speedNames.custom },
                  ]}
                  value={speedKey}
                />
                <QuantityField
                  id="ly-speed"
                  label={strings.speed}
                  onChange={(next) => {
                    setSpeedKey("custom");
                    setSpeedText(next);
                  }}
                  onUnit={setSpeedUnit}
                  unit={speedUnit}
                  unitLabel={strings.speedUnitFor}
                  units={SPEED_UNITS}
                  value={speedText}
                />
              </div>
              {metresPerSecond !== null && metresPerSecond > SPEED_OF_LIGHT ? (
                <ErrorNotice>{strings.tooFast}</ErrorNotice>
              ) : null}
              {metresPerSecond === null || metresPerSecond <= 0 ? (
                <Notice>{strings.speedMissing}</Notice>
              ) : null}
              {travel !== null &&
              metresPerSecond !== null &&
              metresPerSecond <= SPEED_OF_LIGHT ? (
                <StatGrid>
                  <StatCard
                    emphasis
                    label={strings.travelTime}
                    value={durationText(
                      splitDuration(travel),
                      strings,
                      show,
                      integer
                    )}
                  />
                </StatGrid>
              ) : null}
            </section>
          </>
        ) : null}
      </div>
      <Notice>{strings.note}</Notice>
    </div>
  );
}
